import React from 'react';

interface TooltipProps {
  content: React.ReactNode;
  side?: 'top' | 'right' | 'bottom' | 'left';
  delayMs?: number;
  children: React.ReactElement;
}

const SIDE_CLASS: Record<NonNullable<TooltipProps['side']>, string> = {
  top: 'bottom-full left-1/2 -translate-x-1/2 mb-2',
  right: 'left-full top-1/2 -translate-y-1/2 ml-2',
  bottom: 'top-full left-1/2 -translate-x-1/2 mt-2',
  left: 'right-full top-1/2 -translate-y-1/2 mr-2',
};

export const Tooltip: React.FC<TooltipProps> = ({
  content,
  side = 'top',
  children,
}) => {
  const [show, setShow] = React.useState(false);
  const timer = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  const onEnter = () => {
    timer.current = setTimeout(() => setShow(true), 200);
  };
  const onLeave = () => {
    if (timer.current) clearTimeout(timer.current);
    setShow(false);
  };

  return (
    <span
      className="relative inline-flex"
      onMouseEnter={onEnter}
      onMouseLeave={onLeave}
      onFocus={onEnter}
      onBlur={onLeave}
    >
      {children}
      {show && (
        <span
          role="tooltip"
          className={[
            'absolute z-50 whitespace-nowrap rounded-md bg-[var(--surface-2)]',
            'border border-[var(--border-strong)] px-2.5 py-1.5 text-xs text-[var(--text-primary)]',
            'shadow-[var(--shadow-md)] pointer-events-none animate-fade-in',
            SIDE_CLASS[side],
          ].join(' ')}
        >
          {content}
        </span>
      )}
    </span>
  );
};

export default Tooltip;
